import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { devWarn } from "@/lib/logger";

/**
 * ReferralBoost — "Invite 3 friends to jump the queue" tile.
 *
 * Shown on the Landing waitlist success screen. Polls
 * GET /api/waitlist/progress/{code} every 20s so the counter updates live
 * when a friend joins via the share link. Includes prefilled share cards
 * for Twitter/X, WhatsApp, Facebook, LinkedIn, Reddit, Telegram, SMS, Email,
 * plus a copy-link chip.
 */
const withUtm = (url, source) => `${url}${url.includes("?") ? "&" : "?"}utm_source=${source}`;

const CHANNELS = [
  {
    key: "twitter",
    label: "Twitter/X",
    color: "#1da1f2",
    href: ({ url }) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        "I just joined the MoneyHub VIP waitlist — 5 income streams in one dashboard. Payments unlock soon. Join with my link:"
      )}&url=${encodeURIComponent(withUtm(url, "twitter"))}`,
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    color: "#25d366",
    href: ({ url }) =>
      `https://wa.me/?text=${encodeURIComponent(
        "MoneyHub VIP waitlist is open — 5 income streams, payments unlock soon. Join with my link: " + withUtm(url, "whatsapp")
      )}`,
  },
  {
    key: "facebook",
    label: "Facebook",
    color: "#1877f2",
    href: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(withUtm(url, "facebook"))}`,
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    color: "#0a66c2",
    href: ({ url }) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(withUtm(url, "linkedin"))}`,
  },
  {
    key: "reddit",
    label: "Reddit",
    color: "#ff4500",
    href: ({ url }) =>
      `https://www.reddit.com/submit?url=${encodeURIComponent(withUtm(url, "reddit"))}&title=${encodeURIComponent(
        "MoneyHub VIP waitlist — 5 income streams in one dashboard"
      )}`,
  },
  {
    key: "telegram",
    label: "Telegram",
    color: "#26a5e4",
    href: ({ url }) =>
      `https://t.me/share/url?url=${encodeURIComponent(withUtm(url, "telegram"))}&text=${encodeURIComponent(
        "MoneyHub VIP waitlist — 5 income streams, payments unlock soon."
      )}`,
  },
  {
    key: "sms",
    label: "Text SMS",
    color: "#22c55e",
    href: ({ url }) =>
      `sms:?&body=${encodeURIComponent("MoneyHub VIP waitlist is open — join with my link: " + withUtm(url, "sms"))}`,
  },
  {
    key: "email",
    label: "Email",
    color: "#a855f7",
    href: ({ url }) =>
      `mailto:?subject=${encodeURIComponent("Check out MoneyHub VIP")}&body=${encodeURIComponent(
        "Just joined the MoneyHub VIP waitlist — 5 opportunity streams in one dashboard, payments unlock soon. Sign up with my link:\n\n" +
          withUtm(url, "email")
      )}`,
  },
];

export const ReferralBoost = ({ referralCode, shareUrl }) => {
  const [progress, setProgress] = useState(null);
  const [copied, setCopied] = useState(false);
  const [nativeShareOk, setNativeShareOk] = useState(false);

  useEffect(() => {
    setNativeShareOk(typeof navigator !== "undefined" && typeof navigator.share === "function");
  }, []);

  const load = useCallback(async () => {
    if (!referralCode) return;
    try {
      const r = await api.get(`/waitlist/progress/${referralCode}`);
      setProgress(r?.data || null);
    } catch (e) { devWarn("[ReferralBoost] progress poll failed:", e?.message || e); }
  }, [referralCode]);

  useEffect(() => {
    if (!referralCode) return undefined;
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [referralCode, load]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { devWarn("[ReferralBoost] clipboard write blocked:", e?.message || e); }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({
        title: "MoneyHub VIP",
        text: "Just joined the MoneyHub VIP waitlist — 5 income streams in one dashboard. Payments unlock soon.",
        url: withUtm(shareUrl, "native"),
      });
    } catch (e) { devWarn("[ReferralBoost] native share cancelled/failed:", e?.message || e); }
  };

  const target = progress?.queue_jumper_target ?? 3;
  const count = progress?.referral_count ?? 0;
  const unlocked = progress?.queue_jumper_unlocked || count >= target;
  const percent = Math.min(100, Math.round((count / target) * 100));
  const rank = progress?.rank;
  const total = progress?.total;

  return (
    <div
      data-testid="referral-boost"
      className="mt-6 border border-[#60a5fa]/40 bg-[#60a5fa]/5 p-5 text-left"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] font-mono mb-1">
            // Queue-jumper challenge
          </div>
          <div className="text-white font-heading text-lg font-black leading-tight">
            {unlocked
              ? "🎉 Queue-jumper unlocked"
              : `Invite ${target} friends to jump the queue`}
          </div>
          <div className="text-zinc-300 text-xs font-mono mt-1">
            {unlocked
              ? "You'll be in the first batch when payments unlock."
              : `Every join via your link moves you up the list${rank && total ? ` · rank #${rank} of ${total}` : ""}.`}
          </div>
        </div>
        <div
          data-testid="referral-boost-progress-ring"
          className={`shrink-0 rounded-full h-16 w-16 flex items-center justify-center border-2 font-mono text-sm font-black ${
            unlocked ? "border-[#22c55e] text-[#22c55e]" : "border-[#60a5fa] text-[#60a5fa]"
          }`}
          style={{
            background: `conic-gradient(${unlocked ? "#22c55e" : "#60a5fa"} ${percent}%, transparent ${percent}% 100%)`,
          }}
          title={`${count} of ${target}`}
        >
          <div className="bg-black rounded-full h-12 w-12 flex items-center justify-center">
            {count}/{target}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-zinc-800 mb-4 overflow-hidden">
        <div
          data-testid="referral-boost-bar"
          className={`h-full transition-all duration-500 ${unlocked ? "bg-[#22c55e]" : "bg-[#60a5fa]"}`}
          style={{ width: `${percent}%` }}
        />
      </div>

      {/* Share URL row + native share (mobile) */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          data-testid="referral-boost-share-url"
          readOnly
          value={shareUrl}
          onFocus={(e) => e.target.select()}
          className="flex-1 bg-black border border-zinc-800 px-3 py-2 text-white text-xs font-mono truncate"
        />
        <button
          data-testid="referral-boost-copy-btn"
          onClick={copyLink}
          className="shrink-0 text-[10px] tracking-[0.3em] uppercase text-black bg-[#60a5fa] hover:bg-[#93c5fd] px-4 py-2 font-black transition-colors"
        >
          {copied ? "Copied ✓" : "Copy link"}
        </button>
        {nativeShareOk && (
          <button
            data-testid="referral-boost-native-share"
            onClick={nativeShare}
            className="shrink-0 text-[10px] tracking-[0.3em] uppercase text-white border border-zinc-700 hover:border-[#60a5fa] px-4 py-2 font-black transition-colors"
          >
            Share ↗
          </button>
        )}
      </div>

      {/* Share channel grid */}
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono mb-2">
        // pick a channel
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {CHANNELS.map((ch) => (
          <a
            key={ch.key}
            data-testid={`referral-boost-share-${ch.key}`}
            target={ch.key === "sms" || ch.key === "email" ? undefined : "_blank"}
            rel="noopener noreferrer"
            href={ch.href({ url: shareUrl })}
            className="border text-center px-3 py-2 text-[10px] tracking-[0.3em] uppercase font-mono font-bold transition-colors"
            style={{ borderColor: ch.color + "80", color: ch.color }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = ch.color + "20")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            {ch.label}
          </a>
        ))}
      </div>

      {referralCode && (
        <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-mono">
          Your code: <span className="text-white">{referralCode}</span>
          {progress?.badge?.tier && (
            <span
              className="ml-3 border px-2 py-0.5 text-[9px]"
              style={{ borderColor: progress.badge.color, color: progress.badge.color }}
              data-testid="referral-boost-badge"
            >
              {progress.badge.tier}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ReferralBoost;
